import React, { useState } from "react";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { useStoreState } from "ezStore";
import { useMutation } from "@apollo/client";

import { Table } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useConfirm } from "components/hooks/useConfirm";

import { DataFormat, hasPluginPermission, usePermission } from "imports/Utils";
import { FormattedDateWindow } from "utils/DateUtils";

import { ShipFromQuery } from "view/ships/ShipDetails";
import ShipAvailabilityCreateEditDialog from "view/ships/components/dialogs/ShipAvailabilityCreateEditDialog";

import { ChildPropsFromArray } from "types/commons";
import { DeleteAvailabilityExceptionDocument } from "__generated__/view/ships/graphql/ship.mutations.generated";

type Availability = ChildPropsFromArray<ShipFromQuery, "availabilityExceptions"> & { allDay?: boolean };

interface ShipAvailabilityTableProps {
	availabilityExceptions: Availability[];
	defaultAvailability: boolean;
	shipId: number;
	refetch: () => void
}

const ShipAvailabilityTable = ({ availabilityExceptions, defaultAvailability, shipId, refetch }: ShipAvailabilityTableProps) => {
	const session = useStoreState(store => store.session);
	const confirm = useConfirm();
	const { isAdmin, isCompanyUser, isSkipperOrShipManager, isReadOnly } = usePermission();

	const [ deleteAvailabilityException, { loading } ] = useMutation(DeleteAvailabilityExceptionDocument);

	const [ dialog, setDialog ] = useState<{ open: boolean, availability: Availability | null }>({
		open: false,
		availability: null,
	});

	const deleteAvailability = (id: number) => {
		deleteAvailabilityException({
			variables: {
				id: id,
			},
		}).then(() => {
			refetch();
		});
	};

	const permission = isAdmin || isCompanyUser || isSkipperOrShipManager;

	return (
		<React.Fragment>
			<Table
				id="shipAvailability"
				title={ intl.formatMessage({ id: defaultAvailability ? "SHIP_AVAILABILITY_EXCEPTIONS_1" : "SHIP_AVAILABILITY_EXCEPTIONS_2" })}
				contentLoading={loading}
				content={availabilityExceptions.map(item => ({
					...item,
					id: item.id ?? undefined,
					allDay: item.startDate && item.endDate ? (moment(item.startDate).format("HH:mm") === "00:00") && (moment(item.endDate).format("HH:mm") === "23:59") : false,
				}))}
				columns={[ {
					id: "allDay",
					title: intl.formatMessage({ id: "ALL_DAY" }),
					render: (row: Availability) => DataFormat.boolean(row.allDay),
					width: 60,
				}, {
					id: "window",
					title: intl.formatMessage({ id: "FROM_UNTIL" }),
					render: (row: Availability) => <FormattedDateWindow from={row.startDate} to={row.endDate} format={row.allDay ? "ll" : undefined} />,
					width: 250,
				}, {
					id: "location",
					title: `${intl.formatMessage({ id: "START" })} - ${intl.formatMessage({ id: "END" })}`,
					render: (row: Availability) => {
						if (hasPluginPermission(session, "availabilityExceptionSelectMasterdataLocations")) {
							return `${row.startMasterdataLocation?.description} - ${row.endMasterdataLocation?.description}`;
						} else {
							return `${row.startLocation || intl.formatMessage({ id: "UNKNOWN" })} - ${row.endLocation || intl.formatMessage({ id: "UNKNOWN" })}`;
						}
					},
					width: 200,
				}, {
					id: "remark",
					title: intl.formatMessage({ id: "SHIP_AVAILABILITY_REMARK" }),
				} ]}
				actions={permission && !isReadOnly ? [ {
					title: intl.formatMessage({ id: "AGREEMENT_ADD" }),
					onClick: () => {
						setDialog({
							open: true,
							availability: null,
						});
					},
					pinned: true,
				} ] : []}
				rowActions={permission ? [ {
					icon: <EditIcon />,
					onClick: (row) => {
						setDialog({
							open: true,
							availability: row,
						});
					},
				}, {
					icon: <DeleteIcon />,
					onClick: (row) => {
						confirm({
							type: "DELETE",
						}).then(() => {
							deleteAvailability(row.id);
						});
					},
				} ] : undefined }
				disableFooter />

			<ShipAvailabilityCreateEditDialog
				open={dialog.open}
				availability={dialog.availability}
				defaultAvailability={defaultAvailability}
				shipId={shipId}
				onClose={() => {
					setDialog({
						open: false,
						availability: null,
					});
				}}
				refetch={refetch}
			/>
		</React.Fragment>
	);
};

export default ShipAvailabilityTable;
